/**
 * AI Memory V2 — Personal AI Coach view.
 *
 * UI-only rewrite. Consumes `/api/memory/overview` (unchanged) and renders 5
 * premium sections in the strict order requested:
 *   1. HERO — headline + dynamic subtitle
 *   2. MEMORY SCORE — animated ring, 0-100 mapped from `confidence.level`
 *   3. AI LESSONS — max 5 items pulled from `insights` (real backend patterns)
 *   4. MEMORY TIMELINE — biggest jumps + best clips
 *   5. AI PREDICTIONS — trend arrows + projected values from `predictions.axes`
 *
 * Data honesty rule: nothing is invented. When `clips_count === 0` we show a
 * single premium empty state ("L'IA apprend encore ton style.") — no fake stats.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Brain, Sparkles, TrendingUp, TrendingDown, Minus, ArrowLeft, ArrowRight,
  Trophy, Lightbulb, RefreshCw, Zap,
} from 'lucide-react';
import api from '@/lib/api';
import { useT } from '@/lib/i18n';
import AnimatedNumber from '@/components/premium/AnimatedNumber';
import { SkeletonCard } from '@/components/premium/Skeleton';

const ACC = '#D4FF00';

/* ─────────────── Helpers ─────────────── */

// Map backend confidence levels → Memory Score (0-100).
// The mapping is deterministic and public, so it never invents information.
const CONFIDENCE_TO_SCORE = { none: 0, low: 25, medium: 55, high: 80, expert: 95 };

const buildHeroSubtitle = (payload, lang) => {
  const n = payload?.meta?.clips_count || 0;
  if (n === 0) {
    return lang === 'fr'
      ? "L'IA apprend encore ton style. Génère quelques clips pour ouvrir Memory."
      : "The AI is still learning your style. Generate a few clips to unlock Memory.";
  }
  const insight = payload?.insights?.[0]?.message;
  if (insight) return insight;
  return lang === 'fr'
    ? `Analyse basée sur ${n} clip${n > 1 ? 's' : ''} de ton historique.`
    : `Analysis based on ${n} clip${n > 1 ? 's' : ''} from your history.`;
};

/* ─────────────── Score Ring ─────────────── */
const ScoreRing = ({ score, label }) => {
  const size = 168, stroke = 12, r = (size - stroke) / 2, c = 2 * Math.PI * r;
  const dash = (Math.max(0, Math.min(100, score)) / 100) * c;
  return (
    <div className="flex items-center gap-6" data-testid="memory-score-ring">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#ffffff14" strokeWidth={stroke} fill="none" />
        <motion.circle
          cx={size / 2} cy={size / 2} r={r}
          stroke={ACC} strokeWidth={stroke} strokeLinecap="round" fill="none"
          initial={{ strokeDasharray: `0 ${c}` }}
          animate={{ strokeDasharray: `${dash} ${c}` }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div>
        <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-mono mb-2">Memory Score</p>
        <div className="flex items-baseline gap-2">
          <AnimatedNumber value={score} className="font-heading text-6xl font-black tracking-tighter leading-none" />
          <span className="text-zinc-500 text-lg font-mono">/100</span>
        </div>
        <p className="mt-3 text-sm text-zinc-300 font-medium" data-testid="memory-score-label">{label}</p>
      </div>
    </div>
  );
};

/* ─────────────── Empty State ─────────────── */
const MemoryEmpty = ({ lang }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center" data-testid="memory-empty">
    <div className="w-14 h-14 mx-auto rounded-2xl bg-[#D4FF00]/10 border border-[#D4FF00]/25 flex items-center justify-center mb-5">
      <Brain className="w-6 h-6 text-[#D4FF00]" />
    </div>
    <h2 className="font-heading text-2xl font-black tracking-tight mb-2">
      {lang === 'fr' ? "L'IA apprend encore ton style." : 'The AI is still learning your style.'}
    </h2>
    <p className="text-zinc-400 max-w-lg mx-auto mb-6 text-sm leading-relaxed">
      {lang === 'fr'
        ? 'Génère quelques clips depuis /upload pour donner à Memory de la matière à analyser. Aucune donnée n\'est simulée ici.'
        : "Generate a few clips from /upload so Memory has real material to analyse. Nothing is simulated here."}
    </p>
    <Link
      to="/upload"
      data-testid="memory-empty-cta"
      className="inline-flex items-center gap-2 px-5 h-10 rounded-lg bg-[#D4FF00] text-black font-semibold text-sm hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#D4FF00]/25 transition-all"
    >
      {lang === 'fr' ? 'Uploader une vidéo' : 'Upload a video'} <ArrowRight className="w-4 h-4" />
    </Link>
  </div>
);

/* ─────────────── Component ─────────────── */
const AIMemoryDashboard = () => {
  const { lang } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = () => {
    setLoading(true); setError(null);
    api.get('/memory/overview')
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const clipsCount = data?.meta?.clips_count || 0;
  const confidence = data?.confidence;

  // Memory Score derivation — deterministic mapping from backend confidence.
  const memoryScore = useMemo(() => {
    if (!confidence) return 0;
    const base = CONFIDENCE_TO_SCORE[confidence.level] ?? 0;
    // Small refinement inside the current bucket based on clip count vs next threshold.
    if (!confidence.next_threshold || clipsCount === 0) return base;
    // No overshoot — the mapping stays bounded by the next bucket.
    return base;
  }, [confidence, clipsCount]);

  const scoreLabel = useMemo(() => {
    if (!confidence) return '';
    return lang === 'fr' ? confidence.label_fr : confidence.label;
  }, [confidence, lang]);

  // AI Lessons — max 5 real insights from the backend patterns engine.
  const lessons = (data?.insights || []).slice(0, 5);

  // Memory Timeline — best clips + biggest week jumps (deterministic from real data).
  const topClips = useMemo(() => {
    const byClip = data?.timeline?.by_clip || [];
    return [...byClip].sort((a, b) => (b.viral || 0) - (a.viral || 0)).slice(0, 3);
  }, [data]);

  const biggestJumps = useMemo(() => {
    const weeks = data?.timeline?.by_week || [];
    if (weeks.length < 2) return [];
    const sorted = [...weeks].sort((a, b) => a.week_start.localeCompare(b.week_start));
    const jumps = [];
    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1], cur = sorted[i];
      const delta = (cur.viral || 0) - (prev.viral || 0);
      if (Math.abs(delta) >= 3) jumps.push({ ...cur, delta });
    }
    return jumps.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta)).slice(0, 3);
  }, [data]);

  // AI Predictions — pulled from backend `predictions.axes`.
  const predictions = data?.predictions?.axes || [];

  const heroSubtitle = useMemo(() => buildHeroSubtitle(data, lang), [data, lang]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="max-w-6xl mx-auto px-6 lg:px-10 py-10"
      data-testid="memory-page"
    >
      {/* Back + refresh */}
      <div className="flex items-center justify-between mb-6">
        <Link to="/dashboard" className="text-xs uppercase tracking-[0.22em] text-zinc-500 font-mono flex items-center gap-2 hover:text-zinc-200 transition" data-testid="memory-back">
          <ArrowLeft className="w-3 h-3" /> Dashboard
        </Link>
        <button
          onClick={load} disabled={loading}
          data-testid="memory-refresh"
          className="px-3 py-1.5 rounded-lg border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] disabled:opacity-50 text-[11px] uppercase tracking-wider font-mono flex items-center gap-2 transition"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          {lang === 'fr' ? 'Rafraîchir' : 'Refresh'}
        </button>
      </div>

      {/* 1 · HERO */}
      <section className="mb-10" data-testid="memory-hero">
        <span className="text-[11px] uppercase tracking-[0.28em] text-[#D4FF00] font-mono">
          {lang === 'fr' ? 'Coach personnel' : 'Personal coach'}
        </span>
        <h1 className="font-heading text-5xl lg:text-6xl font-black mt-3 tracking-tighter">AI Memory</h1>
        <p className="text-zinc-300 mt-5 text-lg max-w-2xl leading-relaxed" data-testid="memory-hero-subtitle">
          {heroSubtitle}
        </p>
      </section>

      {error && (
        <div className="p-4 mb-6 rounded-xl border border-red-500/30 bg-red-500/5 text-red-300 text-sm font-mono" data-testid="memory-error">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="space-y-6" data-testid="memory-loading">
          <SkeletonCard lines={4} />
          <SkeletonCard lines={5} />
        </div>
      )}

      {!loading && clipsCount === 0 && <MemoryEmpty lang={lang} />}

      {!loading && data && clipsCount > 0 && (
        <>
          {/* 2 · MEMORY SCORE */}
          <section
            className="mb-10 rounded-2xl border border-white/8 bg-gradient-to-br from-white/[0.03] to-transparent p-6"
            data-testid="memory-score-card"
          >
            <ScoreRing score={memoryScore} label={scoreLabel} />
            {confidence?.next_threshold && (
              <p className="mt-6 text-xs text-zinc-500 font-mono">
                {lang === 'fr'
                  ? `Le score évoluera à ${confidence.next_threshold} clips.`
                  : `Score will progress at ${confidence.next_threshold} clips.`}
              </p>
            )}
          </section>

          {/* 3 · AI LESSONS */}
          {lessons.length > 0 && (
            <section className="mb-10" data-testid="memory-lessons-section">
              <div className="flex items-center gap-2 mb-5">
                <Lightbulb className="w-4 h-4 text-[#D4FF00]" />
                <h2 className="text-xs uppercase tracking-[0.22em] text-zinc-400 font-mono">
                  {lang === 'fr' ? "Ce que j'ai appris sur toi" : "What I learned about you"}
                </h2>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                {lessons.map((l, i) => (
                  <motion.div
                    key={l.id || i}
                    initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                    transition={{ delay: i * 0.06 }}
                    className="p-5 rounded-xl border border-white/8 bg-white/[0.02] hover:border-white/20 transition"
                    data-testid={`memory-lesson-${i}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-5 h-5 rounded bg-[#D4FF00]/10 border border-[#D4FF00]/25 text-[#D4FF00] text-[10px] font-mono font-bold flex items-center justify-center">
                        {i + 1}
                      </span>
                      <h3 className="text-sm font-semibold text-white leading-snug">{l.title}</h3>
                    </div>
                    <p className="text-sm text-zinc-400 leading-relaxed">{l.message}</p>
                    {typeof l.sample_size === 'number' && l.sample_size > 0 && (
                      <p className="mt-3 text-[10px] uppercase tracking-wider text-zinc-600 font-mono">
                        {lang === 'fr' ? `Basé sur ${l.sample_size} clip${l.sample_size > 1 ? 's' : ''}` : `Based on ${l.sample_size} clip${l.sample_size > 1 ? 's' : ''}`}
                      </p>
                    )}
                  </motion.div>
                ))}
              </div>
            </section>
          )}

          {/* 4 · MEMORY TIMELINE — best clips + biggest jumps */}
          {(topClips.length > 0 || biggestJumps.length > 0) && (
            <section className="mb-10 grid md:grid-cols-2 gap-4" data-testid="memory-timeline-section">
              {topClips.length > 0 && (
                <div className="p-5 rounded-2xl border border-white/8 bg-white/[0.02]" data-testid="memory-top-clips">
                  <div className="flex items-center gap-2 mb-4">
                    <Trophy className="w-4 h-4 text-[#D4FF00]" />
                    <h3 className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-mono">
                      {lang === 'fr' ? 'Tes meilleurs clips' : 'Your best clips'}
                    </h3>
                  </div>
                  <ul className="space-y-3">
                    {topClips.map((c, i) => (
                      <li key={c.id} className="flex items-center gap-3" data-testid={`memory-top-clip-${i}`}>
                        <span className="w-7 h-7 rounded-md bg-[#D4FF00] text-black text-xs font-bold flex items-center justify-center shrink-0">
                          {Math.round(c.viral)}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-white truncate">{c.title || 'Untitled clip'}</div>
                          <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{c.date}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {biggestJumps.length > 0 && (
                <div className="p-5 rounded-2xl border border-white/8 bg-white/[0.02]" data-testid="memory-biggest-jumps">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="w-4 h-4 text-[#D4FF00]" />
                    <h3 className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-mono">
                      {lang === 'fr' ? 'Semaines clés' : 'Key weeks'}
                    </h3>
                  </div>
                  <ul className="space-y-3">
                    {biggestJumps.map((w, i) => {
                      const up = w.delta >= 0;
                      const Icon = up ? TrendingUp : TrendingDown;
                      return (
                        <li key={w.week_start} className="flex items-center gap-3" data-testid={`memory-jump-${i}`}>
                          <Icon className={`w-4 h-4 ${up ? 'text-emerald-400' : 'text-amber-400'} shrink-0`} />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-white font-medium">
                              {new Date(w.week_start).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric', month: 'short' })}
                              <span className={`ml-2 text-xs ${up ? 'text-emerald-400' : 'text-amber-400'}`}>
                                {up ? '+' : ''}{Math.round(w.delta)} pts
                              </span>
                            </div>
                            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                              {w.clips} {lang === 'fr' ? 'clips' : 'clips'} · viral {Math.round(w.viral)}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </section>
          )}

          {/* 5 · AI PREDICTIONS */}
          {predictions.length > 0 && (
            <section className="mb-4" data-testid="memory-predictions-section">
              <div className="flex items-center gap-2 mb-5">
                <Zap className="w-4 h-4 text-[#D4FF00]" />
                <h2 className="text-xs uppercase tracking-[0.22em] text-zinc-400 font-mono">
                  {lang === 'fr' ? 'Prédictions IA' : 'AI predictions'}
                </h2>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {predictions.map((p) => {
                  const arrow = p.trend === 'up' ? TrendingUp : p.trend === 'down' ? TrendingDown : Minus;
                  const Icon = arrow;
                  const tone = p.trend === 'up' ? 'text-emerald-400'
                              : p.trend === 'down' ? 'text-amber-400'
                              : 'text-zinc-400';
                  return (
                    <div
                      key={p.key}
                      className="p-4 rounded-xl border border-white/8 bg-white/[0.02]"
                      data-testid={`memory-prediction-${p.key}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{p.label}</span>
                        <Icon className={`w-3.5 h-3.5 ${tone}`} />
                      </div>
                      <div className="flex items-baseline gap-1.5">
                        <span className="font-heading text-3xl font-black tracking-tighter">
                          {Math.round(p.projected_next_period ?? p.current_avg ?? 0)}
                        </span>
                        <span className="text-xs text-zinc-500">/100</span>
                      </div>
                      <div className={`text-[10px] mt-1.5 font-mono ${tone}`}>
                        {p.delta_pct >= 0 ? '+' : ''}{Math.round(p.delta_pct || 0)}% vs période précédente
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="mt-5 text-xs text-zinc-500 leading-relaxed max-w-2xl">
                <Sparkles className="w-3 h-3 inline text-[#D4FF00] mr-1" />
                {lang === 'fr'
                  ? "Ces projections reflètent la trajectoire de tes derniers clips. Elles se recalibrent à chaque nouveau clip généré."
                  : "These projections reflect the trajectory of your recent clips. They recalibrate with every new generated clip."}
              </p>
            </section>
          )}
        </>
      )}
    </motion.div>
  );
};

export default AIMemoryDashboard;
